import {
  Backdrop,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { colors } from "../../theme/theme";
import {
  CancelOutlined,
  FlagOutlined,
  ReplyOutlined,
  SendOutlined,
} from "@mui/icons-material";
import { CustomInput } from "../Common/CustomInput";
import axiosInstance from "../../utils/config/config";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const EventComments = ({ eventData, getEventById }) => {
  const scrollRef = useRef(null);

  const { userData } = useSelector((state) => state.user);

  const [comment, setComment] = useState("");
  const [allComments, setAllComments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [moreReplies, setMoreReplies] = useState(false);

  const [replyToComment, setReplyToComment] = useState(null);

  const postComment = async () => {
    console.log("Posting comment:", comment);
    if (!comment) return;
    try {
      const response = await axiosInstance.post("event/createEventComment", {
        eventId: eventData?.id,
        comment: comment,
        user_id: userData?.id,
      });

      if (response.status === 200) {
        setComment("");
        setLoading(false);
        console.log("Comment posted successfully:", response.data);
        getAllComments();
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const postReply = async (id) => {
    if (!comment) return;
    try {
      const response = await axiosInstance.post(
        "event/createEventCommentReply",
        {
          event_id: eventData?.id,
          comment: comment,
          user_id: userData?.id,
          event_comment_id: id,
        }
      );

      if (response.status === 201) {
        setComment("");
        setLoading(false);
        console.log("Comment posted successfully:", response.data);
        getAllComments();
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getAllComments = async () => {
    console.log(eventData?.id, "eventId");
    try {
      const response = await axiosInstance.get(
        `event/getAllComments?eventId=${eventData?.id}`
      );

      if (response.status === 200) {
        setAllComments(response.data);
        console.log("Get Event By Id");
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  };

  useEffect(() => {
    if (eventData?.id) {
      getAllComments();
    }
  }, [eventData]);

  return (
    <Box sx={{ height: "400px", overflowY: "scroll" }} ref={scrollRef}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Typography
        sx={{
          fontSize: "16px",
          color: "#fff",
          mb: 2,
          fontFamily: "Mulish-Light",
        }}
      >
        Comments ({allComments?.length || 0})
      </Typography>

      {allComments?.map((item) => (
        <Box sx={{ display: "flex", flexDirection: "column" }} key={item?.id}>
          <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
            <Typography
              sx={{
                color: colors.secondary,
                fontSize: "14px",
                fontFamily: "Mulish-Medium",
              }}
            >
              {"@" + item?.user?.firstName + ": "}
              <Typography
                component={"span"}
                sx={{
                  color: "#fff",
                  fontSize: "14px",
                  fontFamily: "Mulish-Light",
                }}
              >
                {item?.comment}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.5)",
                  fontFamily: "Mulish-Light",
                }}
              >
                {dayjs(item?.createdAt).format("DD/MM/YYYY")}
              </Typography>
              <IconButton>
                <FlagOutlined sx={{ fontSize: 20, color: colors.secondary }} />
              </IconButton>
            </Stack>
          </Stack>

          {item?.event_comment_reply?.length > 0 &&
            item?.event_comment_reply?.map((replies, index) => {
              const shouldShow = index < 2 || (moreReplies && index >= 2);

              return (
                shouldShow && (
                  <Collapse
                    in={shouldShow}
                    timeout="auto"
                    unmountOnExit
                    key={index}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      mt={1}
                      ml={2}
                    >
                      <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <ReplyOutlined sx={{ fontSize: 20, color: "#fff" }} />

                        <Typography
                          sx={{
                            color: colors.secondary,
                            fontFamily: "Mulish-SemiBold",
                            fontSize: "14px",
                          }}
                        >
                          {"@" + replies?.user?.firstName + " "}
                          <Typography
                            component={"span"}
                            sx={{
                              color: "#fff",
                              fontFamily: "Mulish-Medium",
                              fontSize: "14px",
                            }}
                          >
                            {replies?.comment}
                          </Typography>
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.5)",
                          fontFamily: "Mulish-Regular",
                          fontSize: "12px",
                        }}
                      >
                        {item.time}
                      </Typography>
                    </Stack>
                  </Collapse>
                )
              );
            })}

          {item?.event_comment_reply?.length > 2 && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={0.5}
              mt={1}
              mb={1}
              sx={{
                cursor: "pointer",
                ml: 2,
              }}
              onClick={() => setMoreReplies(!moreReplies)}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "12px",
                  fontFamily: "Mulish-Regular",
                  textDecoration: "underline",
                }}
              >
                {moreReplies ? "hide replies" : "more replies"}
              </Typography>
              <ReplyOutlined sx={{ fontSize: 20, color: "#fff" }} />
            </Stack>
          )}

          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={0.5}
            mt={1}
            mb={1}
            sx={{
              cursor: "pointer",
              ml: 2,
            }}
            onClick={() => {
              setReplyToComment(item);
              setTimeout(() => {
                scrollRef.current?.scrollTo({
                  top: scrollRef.current.scrollHeight,
                  behavior: "smooth",
                });
              }, 100);
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: "12px",
                fontFamily: "Mulish-Regular",
                textDecoration: "underline",
              }}
            >
              Reply
            </Typography>
            <ReplyOutlined sx={{ fontSize: 20, color: "#fff" }} />
          </Stack>
        </Box>
      ))}

      <CustomInput
        value={comment}
        handleChange={(e) => setComment(e.target.value)}
        rightIcon
        Icon={SendOutlined}
        borderColor={"rgba(247,132,25,0.5)"}
        placeholder={
          replyToComment
            ? `Replying to @${replyToComment?.user?.firstName}`
            : "Add a comment..."
        }
        type="text"
        bR={"14px"}
        color={"#fff"}
        width={"100%"}
        onClick={() => {
          if (replyToComment) {
            postReply(replyToComment?.id);
            setReplyToComment(null);
          } else {
            postComment();
          }
        }}
        SecondaryIcon={replyToComment ? CancelOutlined : null}
        secondaryIconOnClick={() => setReplyToComment(null)}
      />
    </Box>
  );
};

export default EventComments;
